<template>
  <FormCheckbox
    v-if="multiple"
    :id="`${name}-${index || 0}-checkbox`"
    :name="`${name}-${index || 0}`"
    :model-value="isChecked"
    :aria-label="optionName"
    :disabled="disabled"
    :readonly="readonly"
    :custom-accent-hex="customAccentHex"
    :checkbox-container-classes="textClass"
    checkbox-size="h-5 w-5 min-w-5 rounded-[5px]"
    text-class="min-w-0 flex-1 text-left"
    items-center
    @update:modelValue="updateModelValue"
  >
    <span v-if="showOptionText" class="flex min-w-0 items-center gap-3 text-left">
      <img v-if="option?.icon" :src="option.icon" class="h-5 w-5 shrink-0" alt=""/>
      <span class="min-w-0 break-words"><slot>{{ optionName }}</slot></span>
      <span v-if="badge" class="shrink-0 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 px-2.5 py-0.5 text-xs font-bold text-white">{{ badge }}</span>
    </span>
  </FormCheckbox>
  <div
    v-else
    class="flex cursor-pointer items-center -ml-0.5"
    :class="[[disabled ? 'opacity-50 line-through' : ''],[textClass]]"
  >
    <input
      type="radio"
      :checked="isChecked"
      :name="name"
      :aria-label="optionName"
      :disabled="readonly || disabled"
      :style="styles"
      tabindex="0"
      class="custom-input appearance-none outline-none border-none focus:ring-2 ring-offset-2"
      @change="updateModelValue"
    />
    <button
      v-if="showOptionText"
      type="button"
      :id="`${name}-${index || 0}`"
      class="flex select-none items-center gap-2"
      @click="updateModelValue">
      <img
        v-if="option?.icon"
        :src="option?.icon"
        class="h-5 w-5"
        alt="Option Icon"
      />
      <slot>{{ optionName }}</slot>
      <span
        v-if="badge"
        class="rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 px-2.5 py-0.5 text-xs font-bold text-white"
      >{{ badge }}</span>
    </button>
  </div>
</template>

<script setup>
import {computed} from "vue";
import FormCheckbox from "./FormCheckbox.vue";
import {addOpacityToHex} from "../helpers/Color";

const emits = defineEmits(["update:modelValue"]);
const props = defineProps({
  modelValue: [String, Number, Boolean, Date, Array],
  multiple: Boolean,
  option: [String, Number, Boolean, Object],
  disabled: Boolean,
  badge: String,
  name: String,
  index: Number,
  showOptionText: {
    type: Boolean,
    default: true,
  },
  customAccentHex: String,
  textClass: {
    type: String,
    default: "text-sm text-slate-500 gap-4",
  },
  readonly: Boolean,
});

const updateModelValue = () => {
  if (props.readonly || props.disabled) return;

  const value = typeof props.option === "object" ? props.option?.value : props.option;

  if (props.multiple) {
    const values = Array.isArray(props.modelValue) ? props.modelValue : [];
    emits("update:modelValue", values.includes(value) ? values.filter(item => item !== value) : [...values, value]);
    return;
  }
  if (props.modelValue === value) return;
  emits("update:modelValue", value);
};

const optionName = computed(() => {
  return typeof props.option === "object" ? props.option?.name : props.option;
});

const optionValue = computed(() => {
  return typeof props.option === "object" && props.option?.value ? props.option?.value : props.option;
});

const isChecked = computed(() => {
  if (props.multiple) return Array.isArray(props.modelValue) && props.modelValue.includes(optionValue.value);
  const digitsOnly = /^\d+$/;
  const isInt = digitsOnly.test(props.modelValue);
  if (isInt && parseInt(props.modelValue) === optionValue.value) return true;
  return props.modelValue === optionValue.value;
});

const styles = computed(() => {
  return {
    "accent-color": props.customAccentHex || "#2c5db6",
    "--accent-color": props.customAccentHex || "#2c5db6",
    "border-color": addOpacityToHex(props.customAccentHex || "#2c5db6", 0.5),
    "--hover-background-color": addOpacityToHex(props.customAccentHex || "#2c5db6", 0.05),
    "--tw-ring-color": addOpacityToHex(props.customAccentHex || "#2c5db6", 0.25),
  };
});
</script>

<style scoped>
.custom-input {
  position: relative;
  transition: background-color 0.2s;
  height: 1.375rem;
  width: 1.375rem;
  border-radius: 50%;
}

.custom-input::before {
  content: "";
  position: absolute;
  inset: 0;
  background-color: transparent;
  border-radius: 50%;
  transition: background-color 0.2s;
  pointer-events: none;
  border: 1px solid #cbd5e1;
}

.custom-input:hover::before,
input:checked::before {
  background-color: var(--hover-background-color);
  border: 1px solid var(--accent-color);
  outline: none;
}

input:checked::after {
  content: "";
  background-color: var(--accent-color);
  color: transparent;
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 50%;
  height: 60%;
  width: 60%;
  transform: translate(-50%, -50%);
}
</style>
